import React, { useMemo, useState } from "react";
import { calcRD, formatINR } from "../../lib/finance";
import { Slider, InputRow, ResultRow, SplitBar, Pills, RightPanel, Divider, HeroValue } from "./CalcUI";

const rdGoals = ["Short Term", "Emergency Fund", "Vacation", "Down Payment"];

const tenureLabel = (months) => {
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y === 0) return `${m} ${m === 1 ? "Month" : "Months"}`;
  if (m === 0) return `${y} ${y === 1 ? "Year" : "Years"}`;
  return `${y}y ${m}m`;
};

export const RDTab = () => {
  const [monthly, setMonthly] = useState(5000);
  const [rate, setRate] = useState(6.45); // typical 2-3 yr bank RD rate, Oct 2026
  const [months, setMonths] = useState(24);
  const [goal, setGoal] = useState("Short Term");

  const result = useMemo(() => calcRD(monthly, rate, months), [monthly, rate, months]);
  const animKey = `rd-${Math.round(result.maturity)}-${monthly}-${rate}-${months}`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2">
      <div className="p-6 sm:p-10">
        <InputRow
          label="Monthly Deposit"
          valueText={`Rs. ${formatINR(monthly)}`}
          valueTestId="rd-monthly-value"
          finePrint={<><span>Rs.100</span><span>Rs.50,000</span></>}
        >
          <Slider value={monthly} min={100} max={50000} step={100} onChange={setMonthly} ariaLabel="RD monthly deposit" />
        </InputRow>

        <InputRow
          label="Annual Interest Rate"
          valueText={`${rate.toFixed(2)}% per annum`}
          valueTestId="rd-rate-value"
          finePrint={<><span>3%</span><span>10% per annum</span></>}
        >
          <Slider value={rate} min={3} max={10} step={0.05} onChange={setRate} ariaLabel="RD rate" />
        </InputRow>

        <InputRow
          label="Tenure"
          valueText={`${tenureLabel(months)} (${months} Months)`}
          valueTestId="rd-tenure-value"
          finePrint={<><span>6 months</span><span>10 years</span></>}
        >
          <Slider value={months} min={6} max={120} step={3} onChange={setMonths} ariaLabel="RD tenure" />
        </InputRow>

        <div>
          <div className="mb-3" style={{ color: "#4A5568", fontSize: 13, fontWeight: 500 }}>Savings Goal</div>
          <Pills items={rdGoals} value={goal} onChange={setGoal} prefix="rd-goal" />
          <p className="mt-3" style={{ color: "#718096", fontSize: 11 }}>
            Interest compounded quarterly (standard Indian RD). Default rate is typical of large banks in October 2026; adjust it to match your bank.
          </p>
        </div>
      </div>

      <RightPanel testId="rd-results">
        <HeroValue label="Maturity Value" value={`Rs. ${formatINR(result.maturity)}`} animKey={animKey} testId="rd-maturity-value" />
        <Divider />
        <div className="space-y-3">
          <ResultRow label="Total Deposited" value={`Rs. ${formatINR(result.invested)}`} testId="rd-row-invested" />
          <ResultRow label="Interest Earned" value={`Rs. ${formatINR(result.interest)}`} valueColor="#1D9E75" testId="rd-row-interest" />
          <ResultRow label="Total Value" value={`Rs. ${formatINR(result.maturity)}`} testId="rd-row-total" />
        </div>
        <Divider />
        <SplitBar
          leftPct={result.investedPct}
          rightPct={result.interestPct}
          leftLabel="Deposited"
          rightLabel="Interest"
          leftColor="#F4A700"
          rightColor="#1D9E75"
          testId="rd-split-bar"
        />
      </RightPanel>
    </div>
  );
};
